import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { NewsBlocks } from '@/components/landing/NewsBlocks'
import { newsApi, type PublicNews } from '@/api/news'
import { useAuthStore } from '@/stores/authStore'
import { getErrorMessage } from '@/utils/error'
import { formatDateDMY } from '@/utils/date'

interface NewsArticleModalProps {
  /** slug открытой новости; null — модалка закрыта */
  slug: string | null
  onClose: () => void
  /** Подписанные параметры preview-ссылки (expires/signature) для черновика */
  signedQuery?: string
  /** Вызывается после успешной отметки прочтения (для обновления счётчиков) */
  onRead?: (slug: string) => void
}

/**
 * Чтение новости в модальном окне — единая точка для виджета дашборда,
 * секции лэндинга и страницы «Все новости».
 */
export function NewsArticleModal({ slug, onClose, signedQuery, onRead }: NewsArticleModalProps) {
  const { t } = useTranslation()
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  const [article, setArticle] = useState<PublicNews | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!slug) {
      setArticle(null)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)
    setArticle(null)

    let cancelled = false

    newsApi
      .getPublicOne(slug, signedQuery)
      .then((news) => {
        if (cancelled) return
        setArticle(news)

        // Прочтение фиксируем только для авторизованных: статистика по пользователям.
        // Ошибка здесь не должна мешать чтению.
        if (isAuthenticated) {
          newsApi
            .markRead(slug)
            .then(() => onRead?.(slug))
            .catch(() => {})
        }
      })
      .catch((err) => {
        if (!cancelled) setError(getErrorMessage(err))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, signedQuery, isAuthenticated])

  return (
    <Dialog open={slug !== null} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        {loading ? <Skeleton variant="text" width="70%" /> : (article?.title ?? t('newsArticle.notFound'))}
        <IconButton
          onClick={onClose}
          aria-label={t('common.close')}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box>
            <Skeleton variant="text" width={140} />
            <Skeleton variant="rounded" height={220} sx={{ my: 2 }} />
            <Skeleton variant="text" />
            <Skeleton variant="text" />
            <Skeleton variant="text" width="80%" />
          </Box>
        ) : article ? (
          <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
              {article.published_at && (
                <Typography variant="caption" color="text.secondary">
                  {t('newsArticle.publishedAt')}: {formatDateDMY(article.published_at)}
                </Typography>
              )}
              {(article.tags ?? []).map((tag) => (
                <Chip
                  key={tag.id}
                  size="small"
                  label={tag.name}
                  sx={{ bgcolor: tag.color, color: '#fff' }}
                />
              ))}
            </Box>

            {article.cover_url && (
              <Box
                component="img"
                src={article.cover_url}
                alt={article.title}
                sx={{ width: '100%', maxHeight: 360, objectFit: 'cover', borderRadius: 2, mb: 2 }}
              />
            )}

            {/* NewsBlocks сам оборачивает тело в .news-article-body (newsArticle.css) */}
            {article.body_json && <NewsBlocks body={article.body_json} />}
          </Box>
        ) : (
          !error && <Typography color="text.secondary">{t('newsArticle.notFoundHint')}</Typography>
        )}
      </DialogContent>
    </Dialog>
  )
}
